import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import {
  formatDate,
  formatDateTime,
  formatHours,
  formatMonthName,
  formatNumber,
  formatPercent,
  TV_UTILIZATION_TARGET_MAX_PCT,
  TV_UTILIZATION_TARGET_MIN_PCT,
  TV_WEEKLY_HOURS_MAX,
  TV_WEEKLY_HOURS_MIN,
  type TvUtilizationAdmin,
  type TvUtilizationAdminPerson,
  type TvUtilizationSettings as Settings,
} from '@codepass/shared';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthProvider';
import {
  Alert,
  Badge,
  Button,
  CardBody,
  CollapsibleCard,
  Input,
  Label,
  Skeleton,
  Table,
  Td,
  Th,
  Tr,
} from '@/components/ui';
import { TvCalendarModal } from '@/components/TvCalendar';

/**
 * Utilization on /admin/tv-dashboard: the state of the sources, the shared weekly norm and target,
 * and a row per user on /admin/brugere, where both can be set on their own.
 *
 * Tallene i tabellen er maaneden til og med i dag, regnet i API'et paa samme maade som skaermen -
 * se apps/api/src/demo/tv-utilization.ts. Her tegnes de kun.
 */

export const UTILIZATION_KEY = ['admin', 'tv-dashboard', 'utilization'] as const;
const PREVIEW_KEY = ['admin', 'tv-dashboard', 'preview'] as const;

/** Felterne som de staar i formularen. Tom tekst = standarden. */
interface PersonDraft {
  weekly_hours: string;
  target_pct: string;
  hidden: boolean;
}

interface Draft {
  default_weekly_hours: string;
  default_target_pct: string;
  people: Record<string, PersonDraft>;
}

function toText(value: number | null): string {
  return value === null ? '' : formatNumber(value, Number.isInteger(value) ? 0 : 2);
}

function draftOf(data: TvUtilizationAdmin): Draft {
  return {
    default_weekly_hours: toText(data.settings.default_weekly_hours),
    default_target_pct: toText(data.settings.default_target_pct),
    people: Object.fromEntries(
      data.people.map((person) => [
        person.user_id,
        {
          weekly_hours: toText(person.weekly_hours),
          target_pct: toText(person.target_pct),
          hidden: person.hidden,
        },
      ]),
    ),
  };
}

/**
 * Et tal med komma eller punktum som decimaltegn - "37,5" og "37.5" er det samme. Ingen tusindtal:
 * en normtid og et maal kommer aldrig i naerheden. NaN, naar feltet ikke er et tal.
 */
function parseNumber(text: string): number {
  const cleaned = text.trim().replace(',', '.');
  return cleaned.length === 0 ? Number.NaN : Number(cleaned);
}

/**
 * Formularen som indstillinger, eller den foerste fejl. Kun for UX'ens skyld - API'et validerer
 * det samme og har det sidste ord.
 */
function readDraft(draft: Draft): { settings: Settings } | { error: string } {
  const weekly = (text: string, who: string): number | string => {
    const value = parseNumber(text);
    if (!Number.isFinite(value) || value < TV_WEEKLY_HOURS_MIN || value > TV_WEEKLY_HOURS_MAX) {
      return `Normtiden ${who} skal være mellem ${TV_WEEKLY_HOURS_MIN} og ${TV_WEEKLY_HOURS_MAX} timer om ugen.`;
    }
    return value;
  };
  const target = (text: string, who: string): number | string => {
    const value = parseNumber(text);
    if (
      !Number.isInteger(value) ||
      value < TV_UTILIZATION_TARGET_MIN_PCT ||
      value > TV_UTILIZATION_TARGET_MAX_PCT
    ) {
      return `Målet ${who} skal være et helt antal procent mellem ${formatPercent(TV_UTILIZATION_TARGET_MIN_PCT)} og ${formatPercent(TV_UTILIZATION_TARGET_MAX_PCT)}.`;
    }
    return value;
  };

  const defaultWeekly = weekly(draft.default_weekly_hours, 'for alle');
  if (typeof defaultWeekly === 'string') return { error: defaultWeekly };
  const defaultTarget = target(draft.default_target_pct, 'for alle');
  if (typeof defaultTarget === 'string') return { error: defaultTarget };

  const people: Settings['people'] = [];
  for (const [id, person] of Object.entries(draft.people)) {
    const ownWeekly = person.weekly_hours.trim() ? weekly(person.weekly_hours, 'pr. person') : null;
    if (typeof ownWeekly === 'string') return { error: ownWeekly };
    const ownTarget = person.target_pct.trim() ? target(person.target_pct, 'pr. person') : null;
    if (typeof ownTarget === 'string') return { error: ownTarget };

    people.push({
      user_id: id,
      weekly_hours: ownWeekly,
      target_pct: ownTarget,
      hidden: person.hidden,
    });
  }

  return {
    settings: { default_weekly_hours: defaultWeekly, default_target_pct: defaultTarget, people },
  };
}

function statusOf(
  person: TvUtilizationAdminPerson,
): { label: string; tone: 'neutral' | 'warning' } | null {
  if (person.on_screen) return null;
  if (person.hidden) return { label: 'Skjult', tone: 'neutral' };
  if (!person.active) return { label: 'Deaktiveret', tone: 'neutral' };
  if (!person.has_recent_time) return { label: 'Ingen tid i OnePro', tone: 'neutral' };
  return { label: 'Ingen timer til rådighed', tone: 'neutral' };
}

export function TvUtilizationSettings() {
  const queryClient = useQueryClient();
  /* 'Se TV-dashboard' uden 'Ændr': tallene og indstillingerne til at laese. */
  const canEdit = useAuth().can('tv_dashboard.manage');

  const query = useQuery({
    queryKey: UTILIZATION_KEY,
    queryFn: () => api.get<TvUtilizationAdmin>('/tv-dashboard/utilization'),
  });

  const [message, setMessage] = useState<{ tone: 'error' | 'info'; text: string } | null>(null);

  /** Formularen, mens den redigeres. Null = uroert; saa vises det gemte. */
  const [draft, setDraft] = useState<Draft | null>(null);
  const saved = query.data ? draftOf(query.data) : null;
  const current = draft ?? saved;
  const read = current ? readDraft(current) : null;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(saved);

  /* Optimistisk: indstillingerne staar som gemt med det samme; tallene foelger med svaret. */
  const save = useMutation({
    mutationFn: (settings: Settings) =>
      api.put<TvUtilizationAdmin>('/tv-dashboard/utilization', settings),
    onMutate: async (settings) => {
      await queryClient.cancelQueries({ queryKey: UTILIZATION_KEY });
      const previous = queryClient.getQueryData<TvUtilizationAdmin>(UTILIZATION_KEY);
      const attempted = draft;
      if (previous) {
        const own = new Map(settings.people.map((person) => [person.user_id, person]));
        queryClient.setQueryData<TvUtilizationAdmin>(UTILIZATION_KEY, {
          ...previous,
          settings: {
            default_weekly_hours: settings.default_weekly_hours,
            default_target_pct: settings.default_target_pct,
          },
          people: previous.people.map((person) => ({
            ...person,
            ...(own.get(person.user_id) ?? {}),
          })),
        });
      }
      setDraft(null);
      setMessage(null);
      return { previous, attempted };
    },
    onError: (cause, _settings, context) => {
      queryClient.setQueryData(UTILIZATION_KEY, context?.previous);
      // Det forsoegte bliver staaende i formularen, saa det kan gemmes igen uden at blive tastet om.
      setDraft(context?.attempted ?? null);
      setMessage({
        tone: 'error',
        text: cause instanceof ApiError ? cause.message : 'Kunne ikke gemme faktureringsgraden.',
      });
    },
    onSuccess: async (result) => {
      queryClient.setQueryData(UTILIZATION_KEY, result);
      setMessage({
        tone: 'info',
        text: 'Normtid og mål er gemt. Skærmen viser dem ved sin næste opdatering, senest om fem minutter.',
      });
      await queryClient.invalidateQueries({ queryKey: PREVIEW_KEY });
    },
  });

  const data = query.data;
  const [calendarOpen, setCalendarOpen] = useState(false);

  const edit = (change: (value: Draft) => Draft): void => {
    if (current) setDraft(change(current));
  };
  const editPerson = (id: string, change: Partial<PersonDraft>): void =>
    edit((value) => ({
      ...value,
      people: {
        ...value.people,
        [id]: { ...(value.people[id] as PersonDraft), ...change },
      },
    }));

  return (
    <CollapsibleCard
      title="Faktureringsgrad"
      description="Fakturerbare timer i OnePro delt med normtiden minus helligdage, fravær fra fraværskalenderen i Outlook og fridage, skole og kursus fra kalenderen. Fakturerbart er tid på opgaver hos en kunde eller på en tavle, der er koblet til en aftale."
      actions={
        <Button variant="secondary" size="sm" onClick={() => setCalendarOpen(true)}>
          <CalendarDays className="h-4 w-4" />
          Kalender
        </Button>
      }
    >
      <TvCalendarModal
        open={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        onChanged={() => {
          void queryClient.invalidateQueries({ queryKey: UTILIZATION_KEY });
          void queryClient.invalidateQueries({ queryKey: PREVIEW_KEY });
        }}
      />
      <CardBody className="space-y-4">
        {message ? (
          <Alert tone={message.tone} onDismiss={() => setMessage(null)}>
            {message.text}
          </Alert>
        ) : null}

        {query.isLoading ? (
          <Skeleton className="h-40" />
        ) : query.error || !data || !current ? (
          <Alert tone="error">Kunne ikke hente faktureringsgradens indstillinger.</Alert>
        ) : (
          <>
            <SourceStatus source={data.source} />

            <fieldset disabled={!canEdit} className="m-0 min-w-0 space-y-4 border-0 p-0">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <Label htmlFor="default-weekly-hours">Normtid pr. uge, alle</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="default-weekly-hours"
                      inputMode="decimal"
                      className="w-24"
                      value={current.default_weekly_hours}
                      onChange={(event) =>
                        edit((value) => ({ ...value, default_weekly_hours: event.target.value }))
                      }
                    />
                    <span className="text-sm text-ink-muted">timer</span>
                  </div>
                </div>
                <div>
                  <Label htmlFor="default-target">Mål, alle</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      id="default-target"
                      inputMode="numeric"
                      className="w-20"
                      value={current.default_target_pct}
                      onChange={(event) =>
                        edit((value) => ({ ...value, default_target_pct: event.target.value }))
                      }
                    />
                    <span className="text-sm text-ink-muted">%</span>
                  </div>
                </div>
                <p className="max-w-md text-xs text-ink-muted">
                  Gælder alle, der ikke har deres egen værdi i tabellen. Et tomt felt i tabellen
                  følger værdien her. Normtid gange mål er også de ledige timer under Rapporter →
                  Kapacitet og på storskærm 2 — fx 37 × 70 % = 25,9 timer om ugen.
                </p>
              </div>

              {data.people.length > 0 ? (
                <>
                  <PeriodLine data={data} />
                  <Table>
                    <thead>
                      <tr>
                        <Th>Medarbejder</Th>
                        <Th>Normtid</Th>
                        <Th>Mål</Th>
                        <Th>Vis</Th>
                        <Th className="text-right">Fravær</Th>
                        <Th className="text-right" title="Fridage, skole og kursus fra kalenderen">
                          Fri
                        </Th>
                        <Th className="text-right">Til rådighed</Th>
                        <Th className="text-right">Fakturerbart</Th>
                        <Th className="text-right">Grad</Th>
                        <Th className="text-right" title="Tid på interne opgaver i OnePro">
                          Internt
                        </Th>
                        <Th
                          className="text-right"
                          title="Al tiden, fakturerbar og intern, delt med timerne til rådighed"
                        >
                          Inkl. internt
                        </Th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.people.map((person) => {
                        const row = current.people[person.user_id];
                        if (!row) return null;
                        const status = statusOf(person);
                        return (
                          <Tr key={person.user_id}>
                            <Td>
                              <p className="font-medium">{person.name}</p>
                              <p className="text-xs text-ink-muted">{person.email}</p>
                            </Td>
                            <Td>
                              <Input
                                aria-label={`Normtid for ${person.name}`}
                                inputMode="decimal"
                                className="w-20"
                                placeholder={current.default_weekly_hours}
                                value={row.weekly_hours}
                                onChange={(event) =>
                                  editPerson(person.user_id, {
                                    weekly_hours: event.target.value,
                                  })
                                }
                              />
                            </Td>
                            <Td>
                              <Input
                                aria-label={`Mål for ${person.name}`}
                                inputMode="numeric"
                                className="w-16"
                                placeholder={current.default_target_pct}
                                value={row.target_pct}
                                onChange={(event) =>
                                  editPerson(person.user_id, {
                                    target_pct: event.target.value,
                                  })
                                }
                              />
                            </Td>
                            <Td>
                              <input
                                type="checkbox"
                                className="h-4 w-4 cursor-pointer accent-brand"
                                aria-label={`Vis ${person.name} på skærmen`}
                                checked={!row.hidden}
                                onChange={(event) =>
                                  editPerson(person.user_id, {
                                    hidden: !event.target.checked,
                                  })
                                }
                              />
                            </Td>
                            <Td className="tabular text-right">
                              {formatNumber(person.absence_days, 1)} d
                            </Td>
                            <Td className="tabular text-right">
                              {formatNumber(person.off_days, 1)} d
                            </Td>
                            <Td className="tabular text-right">
                              {formatHours(person.available_hours)}
                            </Td>
                            <Td className="tabular text-right">
                              {formatHours(person.billable_hours)}
                            </Td>
                            <Td className="text-right">
                              {status ? (
                                <Badge tone={status.tone}>{status.label}</Badge>
                              ) : (
                                <span
                                  className={
                                    (person.pct ?? 0) >= person.target_pct_effective
                                      ? 'tabular font-semibold text-emerald-700'
                                      : 'tabular font-semibold'
                                  }
                                >
                                  {formatPercent(person.pct)}
                                </span>
                              )}
                            </Td>
                            <Td className="tabular text-right">
                              {formatHours(person.internal_hours)}
                            </Td>
                            <Td className="tabular text-right">
                              {status ? null : formatPercent(person.total_pct)}
                            </Td>
                          </Tr>
                        );
                      })}
                    </tbody>
                  </Table>
                  <p className="text-sm">
                    <span className="font-semibold">Huset: {formatPercent(data.house.pct)}</span>
                    <span className="text-ink-muted">
                      {' '}
                      af mål {formatPercent(data.house.target_pct)} ·{' '}
                      {formatHours(data.house.billable_hours)} fakturerbare af{' '}
                      {formatHours(data.house.available_hours)} timer til rådighed · inkl. internt{' '}
                      {formatPercent(data.house.total_pct)} (
                      {formatHours(data.house.internal_hours)} interne timer)
                    </span>
                  </p>
                </>
              ) : data.source.configured && !data.source.error ? (
                <p className="text-sm text-ink-muted">Der er ingen brugere på /admin/brugere.</p>
              ) : null}
            </fieldset>

            {read && 'error' in read && dirty ? <Alert tone="warning">{read.error}</Alert> : null}

            {canEdit ? (
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  disabled={!dirty || !read || 'error' in read}
                  loading={save.isPending}
                  onClick={() => {
                    if (read && 'settings' in read) save.mutate(read.settings);
                  }}
                >
                  Gem
                </Button>
                <Button variant="ghost" disabled={!dirty} onClick={() => setDraft(null)}>
                  Fortryd
                </Button>
              </div>
            ) : null}
          </>
        )}
      </CardBody>
    </CollapsibleCard>
  );
}

function PeriodLine({ data }: { data: TvUtilizationAdmin }) {
  const { period } = data;
  const holidays =
    period.holidays.length === 0
      ? 'ingen helligdage'
      : `helligdage: ${period.holidays.map((day) => `${day.name} ${formatDate(day.date)}`).join(', ')}`;

  return (
    <p className="text-xs text-ink-muted">
      {formatMonthName(period.start)} til og med {formatDate(period.through)} ·{' '}
      {period.working_days} arbejdsdage · {holidays}. Fravær er ferie og sygdom fra Outlook, Fri er
      fridage, skole og kursus fra kalenderen. I dag tæller kun med de fakturerbare timer, der er
      registreret indtil nu, så dagen ikke trækker graden ned, før den er gået. Internt arbejde
      tæller ikke i graden; Inkl. internt er al tiden, og der tæller i dag med al den registrerede
      tid. Samme tal som skærmen.
    </p>
  );
}

/**
 * Where the people and the absence come from, and what did not fit. An event in the absence
 * calendar that names no one counts for no one, so it is listed here rather than lost.
 */
function SourceStatus({ source }: { source: TvUtilizationAdmin['source'] }) {
  if (!source.configured) {
    return (
      <Alert tone="warning">
        Codepass mangler forbindelsen til Microsoft. Sæt DYNAMICS_TENANT_ID, DYNAMICS_CLIENT_ID og
        DYNAMICS_CLIENT_SECRET på /admin/integrationer.
      </Alert>
    );
  }

  const unmatched = source.unmatched_absences;

  return (
    <div className="space-y-2 rounded-md border border-line p-3">
      <p className="text-sm">
        <Badge tone={source.error ? 'danger' : 'success'}>
          {source.error ? 'Fejl' : 'Forbundet'}
        </Badge>{' '}
        Medarbejderne er brugerne på /admin/brugere, og fraværet kommer fra{' '}
        <span className="font-mono">{source.mailbox}</span>.{' '}
        {source.fetched_at
          ? `Hentet ${formatDateTime(source.fetched_at)}.`
          : 'Fraværet er ikke hentet endnu.'}
      </p>
      <p className="text-sm">
        Fraværskalenderen er sidst opdateret{' '}
        <span className="font-medium">
          {source.calendar_updated_at ? formatDateTime(source.calendar_updated_at) : 'ukendt'}
        </span>
        . Reglen Fraværskalender på /admin/regler giver besked, når det er for længe siden.
      </p>
      <p className="text-xs text-ink-muted">
        Fraværet kobles til en bruger via det fulde navn i Entra ID og brugerens email. En bruger
        uden tid i OnePro det seneste halve år står ikke på skærmen.
      </p>
      {source.error ? <Alert tone="error">{source.error}</Alert> : null}
      {unmatched.length > 0 ? (
        <Alert tone="warning">
          <p>
            {unmatched.length === 1
              ? '1 begivenhed i fraværskalenderen nævner ingen bruger og tæller ikke med.'
              : `${unmatched.length} begivenheder i fraværskalenderen nævner ingen bruger og tæller ikke med.`}{' '}
            Navnet i emnet skal stå som det fulde navn i Entra ID, og personens email skal være en
            bruger på /admin/brugere.
          </p>
          <ul className="mt-1 list-disc pl-5">
            {unmatched.map((absence, index) => (
              <li key={`${absence.start_date}-${index}`}>
                {absence.subject ?? 'Uden emne'} · {formatDate(absence.start_date)}
                {absence.end_date !== absence.start_date
                  ? ` til ${formatDate(absence.end_date)}`
                  : ''}
              </li>
            ))}
          </ul>
        </Alert>
      ) : null}
    </div>
  );
}
